import { useEffect, useState } from 'react';
import { Edit } from 'lucide-react';

const BASE_URL = 'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/products';

function getAuthHeaders() {
  const token = (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('access_token'))
    || (typeof localStorage !== 'undefined' && localStorage.getItem('access_token'));
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function ProductBenefits({ product, onSave }) {
  const [editMode, setEditMode] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const showOverlay = (message) => {
    setOverlayMessage(message);
    setIsOverlayOpen(true);
    setTimeout(() => setIsOverlayOpen(false), 3000);
  };

  const [benefits, setBenefits] = useState({
    time_credit_amount: String(product.benefits?.time_credit?.amount || ''),
    time_credit_unit: product.benefits?.time_credit?.unit || 'minutes',
    time_credit_never_expire: product.benefits?.time_credit?.never_expire || false,
    time_credit_expire_days: String(product.benefits?.time_credit?.expire_after_days || ''),
    money_credit_amount: String(product.benefits?.money_credit?.amount || ''),
    money_credit_never_expire: product.benefits?.money_credit?.never_expire || false,
    money_credit_expire_days: String(product.benefits?.money_credit?.expire_after_days || ''),
    passes_count: String(product.benefits?.passes?.count || ''),
    passes_never_expire: product.benefits?.passes?.never_expire || false,
    passes_expire_days: String(product.benefits?.passes?.expire_after_days || ''),
    printing_credit_amount: String(product.benefits?.printing_credit?.amount || ''),
    printing_credit_never_expire: product.benefits?.printing_credit?.never_expire || false,
    printing_credit_expire_days: String(product.benefits?.printing_credit?.expire_after_days || '')
  });

  // Sync state when product updates without refreshing
  useEffect(() => {
    setBenefits({
      time_credit_amount: String(product.benefits?.time_credit?.amount || ''),
      time_credit_unit: product.benefits?.time_credit?.unit || 'minutes',
      time_credit_never_expire: product.benefits?.time_credit?.never_expire || false,
      time_credit_expire_days: String(product.benefits?.time_credit?.expire_after_days || ''),
      money_credit_amount: String(product.benefits?.money_credit?.amount || ''),
      money_credit_never_expire: product.benefits?.money_credit?.never_expire || false,
      money_credit_expire_days: String(product.benefits?.money_credit?.expire_after_days || ''),
      passes_count: String(product.benefits?.passes?.count || ''),
      passes_never_expire: product.benefits?.passes?.never_expire || false,
      passes_expire_days: String(product.benefits?.passes?.expire_after_days || ''),
      printing_credit_amount: String(product.benefits?.printing_credit?.amount || ''),
      printing_credit_never_expire: product.benefits?.printing_credit?.never_expire || false,
      printing_credit_expire_days: String(product.benefits?.printing_credit?.expire_after_days || '')
    });
  }, [product]);


  const handleSave = async () => {
    try {
      const response = await fetch(`${BASE_URL}/updateProducts/${product.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          benefits: {
            time_credit: {
              amount: parseInt(benefits.time_credit_amount) || 0,
              unit: benefits.time_credit_unit,
              never_expire: benefits.time_credit_never_expire,
              ...(benefits.time_credit_expire_days && !benefits.time_credit_never_expire && {
                expireInDays: parseInt(benefits.time_credit_expire_days)
              })
            },
            money_credit: {
              amount: parseInt(benefits.money_credit_amount) || 0,
              never_expire: benefits.money_credit_never_expire,
              ...(benefits.money_credit_expire_days && !benefits.money_credit_never_expire && {
                expireInDays: parseInt(benefits.money_credit_expire_days)
              })
            },
            passes: {
              count: parseInt(benefits.passes_count) || 0,
              never_expire: benefits.passes_never_expire,
              ...(benefits.passes_expire_days && !benefits.passes_never_expire && {
                expireInDays: parseInt(benefits.passes_expire_days)
              })
            },
            printing_credit: {
              amount: parseInt(benefits.printing_credit_amount) || 0,
              never_expire: benefits.printing_credit_never_expire,
              ...(benefits.printing_credit_expire_days && !benefits.printing_credit_never_expire && {
                expireInDays: parseInt(benefits.printing_credit_expire_days)
              })
            }
          }
        })
      });

      if (!response.ok) throw new Error('Failed to update benefits');
      
      setEditMode(false);
      await onSave();
      showOverlay('Benefits updated successfully');
    } catch (error) {
      console.error('Error updating benefits:', error);
      showOverlay('Failed to update benefits: ' + error.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-md font-medium text-gray-900">Benefits Details</h3>
        {!editMode ? (
          <button
            onClick={() => setEditMode(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => setEditMode(false)}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        )}
      </div>

      {/* Time Credit */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Time Credit</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount
            </label>
            {editMode ? (
              <input
                type="number"
                value={benefits.time_credit_amount}
                onChange={(e) => setBenefits({ ...benefits, time_credit_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.time_credit_amount || '0'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unit
            </label>
            {editMode ? (
              <select
                value={benefits.time_credit_unit}
                onChange={(e) => setBenefits({ ...benefits, time_credit_unit: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900 capitalize">
                {benefits.time_credit_unit}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Never Expire
            </label>
            {editMode ? (
              <select
                value={benefits.time_credit_never_expire ? 'yes' : 'no'}
                onChange={(e) => setBenefits({ ...benefits, time_credit_never_expire: e.target.value === 'yes' })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.time_credit_never_expire ? 'Yes' : 'No'}
              </div>
            )}
          </div>

          {!benefits.time_credit_never_expire && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expire After (days)
              </label>
              {editMode ? (
                <input
                  type="number"
                  value={benefits.time_credit_expire_days}
                  onChange={(e) => setBenefits({ ...benefits, time_credit_expire_days: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                  {benefits.time_credit_expire_days || 'N/A'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Money Credit */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Money Credit</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount
            </label>
            {editMode ? (
              <input
                type="number"
                value={benefits.money_credit_amount}
                onChange={(e) => setBenefits({ ...benefits, money_credit_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.money_credit_amount || '0'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Never Expire
            </label>
            {editMode ? (
              <select
                value={benefits.money_credit_never_expire ? 'yes' : 'no'}
                onChange={(e) => setBenefits({ ...benefits, money_credit_never_expire: e.target.value === 'yes' })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.money_credit_never_expire ? 'Yes' : 'No'}
              </div>
            )}
          </div>
        </div>
        {!benefits.money_credit_never_expire && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expire After (days)
              </label>
              {editMode ? (
                <input
                  type="number"
                  value={benefits.money_credit_expire_days}
                  onChange={(e) => setBenefits({ ...benefits, money_credit_expire_days: e.target.value })}
                  className="w-1/2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="w-1/2 px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                  {benefits.money_credit_expire_days || 'N/A'}
                </div>
              )}
            </div>
          )}
      </div>

      {/* Passes */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Passes</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Count
            </label>
            {editMode ? (
              <input
                type="number"
                value={benefits.passes_count}
                onChange={(e) => setBenefits({ ...benefits, passes_count: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.passes_count || '0'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Never Expire
            </label>
            {editMode ? (
              <select
                value={benefits.passes_never_expire ? 'yes' : 'no'}
                onChange={(e) => setBenefits({ ...benefits, passes_never_expire: e.target.value === 'yes' })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.passes_never_expire ? 'Yes' : 'No'}
              </div>
            )}
          </div>
        </div>
        {!benefits.passes_never_expire && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expire After (days)
              </label>
              {editMode ? (
                <input
                  type="number"
                  value={benefits.passes_expire_days}
                  onChange={(e) => setBenefits({ ...benefits, passes_expire_days: e.target.value })}
                  className="w-1/2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="w-1/2 px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                  {benefits.passes_expire_days || 'N/A'}
                </div>
              )}
            </div>
          )}
      </div>

      {/* Printing Credit */}
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Printing Credit</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount
            </label>
            {editMode ? (
              <input
                type="number"
                value={benefits.printing_credit_amount}
                onChange={(e) => setBenefits({ ...benefits, printing_credit_amount: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.printing_credit_amount || '0'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Never Expire
            </label>
            {editMode ? (
              <select
                value={benefits.printing_credit_never_expire ? 'yes' : 'no'}
                onChange={(e) => setBenefits({ ...benefits, printing_credit_never_expire: e.target.value === 'yes' })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : (
              <div className="px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                {benefits.printing_credit_never_expire ? 'Yes' : 'No'}
              </div>
            )}
          </div>
        </div>
        {!benefits.printing_credit_never_expire && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expire After (days)
              </label>
              {editMode ? (
                <input
                  type="number"
                  value={benefits.printing_credit_expire_days}
                  onChange={(e) => setBenefits({ ...benefits, printing_credit_expire_days: e.target.value })}
                  className="w-1/2 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <div className="w-1/2 px-4 py-2 bg-gray-50 rounded-lg text-gray-900">
                  {benefits.printing_credit_expire_days || 'N/A'}
                </div>
              )}
            </div>
          )}
      </div>
      {/* Overlay Message */}
      {isOverlayOpen && (
        <div className="fixed top-4 right-4 z-[100] animate-slide-in">
          <div className="bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg">
            <p className="text-sm">{overlayMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
}